import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, ChevronLeft, ChevronRight, Clock, FileText, Loader2, Lock, Search, Sparkles, Wand2, X } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { useLanguage } from "@/components/language-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Mascot } from "@/components/assistant/mascot";
import { askAssistant } from "@/components/assistant/guide";
import { aiButtonState, useAiStatus } from "@/components/library/ai-adjust-dialog";
import { categoryName, fetchCategories, fetchMine, searchModels, type ContractCategory } from "@/lib/library-client";

const PAGE_SIZE = 24;

const TEXTS = {
  pt: {
    title: "Biblioteca de modelos",
    subtitle: "Contratos e documentos brasileiros, prontos para preencher e assinar",
    search: "Buscar por nome ou assunto (ex.: locação, prestação de serviços, procuração)",
    all: "Todos",
    results: (n: number, q: string) => n === 1 ? `1 modelo para “${q}”` : `${n} modelos para “${q}”`,
    inCategory: (n: number) => n === 1 ? "1 modelo" : `${n} modelos`,
    empty: "Nenhum modelo com essas palavras.",
    emptyHint: "Tente termos mais simples (locação, serviços, procuração) ou escolha uma categoria.",
    fields: (n: number) => n === 0 ? "texto livre" : n === 1 ? "1 campo" : `${n} campos`,
    use: "Usar modelo",
    page: (a: number, b: number, n: number) => `${a}–${b} de ${n}`,
    prev: "Anterior",
    next: "Próxima",
    hello: "Oi! Escolha uma categoria ou me diga o que você precisa. São modelos brasileiros, prontos para preencher.",
    found: (n: number) => n === 0 ? "Não achei com essas palavras. Tente algo mais simples, tipo “locação” ou “serviços”." : n === 1 ? "Achei um modelo. Abre e preenche os campos destacados." : `Achei ${n} modelos. Abre um e preenche os campos destacados — depois é só assinar.`,
    browsing: (name: string) => `${name}: clica em um modelo para preencher.`,
    ask: "Pedir ajuda ao FillSign",
    askMessage: "Preciso de um modelo de contrato. Pode me ajudar a escolher?",
    note: "Modelos brasileiros de referência, em português e de acordo com a lei do Brasil. Revise o texto antes de enviar para assinatura; em casos importantes, consulte um profissional. Precisa em inglês? Abra o modelo e peça a tradução no Ajustar com IA: o contrato continua regido pela lei brasileira.",
    categories: "Categorias",
    aiPromo: "Abra um modelo e use o Ajustar com IA para incluir uma cláusula, simplificar a linguagem ou traduzir para inglês.",
    aiPaid: "planos pagos",
    mine: "Meus modelos",
    mineHint: (days: number) => `Cópias ajustadas com IA. Só você vê; cada uma fica ${days} dias e depois é apagada.`,
    daysLeft: (n: number) => n <= 1 ? "expira em 1 dia" : `expira em ${n} dias`,
    open: "Abrir",
  },
  en: {
    title: "Model library",
    subtitle: "Brazilian contracts and documents (in Portuguese), ready to fill in and sign",
    search: "Search by name or subject (e.g. locação, serviços, procuração)",
    all: "All",
    results: (n: number, q: string) => n === 1 ? `1 model for “${q}”` : `${n} models for “${q}”`,
    inCategory: (n: number) => n === 1 ? "1 model" : `${n} models`,
    empty: "No model matches those words.",
    emptyHint: "Try simpler Portuguese terms (locação, serviços, procuração) or pick a category.",
    fields: (n: number) => n === 0 ? "free text" : n === 1 ? "1 field" : `${n} fields`,
    use: "Use model",
    page: (a: number, b: number, n: number) => `${a}–${b} of ${n}`,
    prev: "Previous",
    next: "Next",
    hello: "Hi! Pick a category or tell me what you need. These are Brazilian models (in Portuguese), ready to fill in.",
    found: (n: number) => n === 0 ? "Nothing with those words. Try something simpler, like “locação” or “serviços”." : n === 1 ? "Found one model. Open it and fill in the highlighted fields." : `Found ${n} models. Open one and fill in the highlighted fields — then just sign.`,
    browsing: (name: string) => `${name}: click a model to fill it in.`,
    ask: "Ask FillSign for help",
    askMessage: "I need a contract model. Can you help me choose one?",
    note: "Brazilian reference models, in Portuguese and under Brazilian law. Review the text before sending it for signature; for important matters, consult a professional. Need it in English? Open the model and ask for a translation in Adjust with AI: the contract is still governed by Brazilian law.",
    categories: "Categories",
    aiPromo: "Open a model and use Adjust with AI to add a clause, simplify the language or translate it into English.",
    aiPaid: "paid plans",
    mine: "My templates",
    mineHint: (days: number) => `Copies adjusted with AI. Only you can see them; each one stays ${days} days and is then deleted.`,
    daysLeft: (n: number) => n <= 1 ? "expires in 1 day" : `expires in ${n} days`,
    open: "Open",
  },
};

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function Library() {
  const { language } = useLanguage();
  const t = language === "en" ? TEXTS.en : TEXTS.pt;
  const [, setLocation] = useLocation();
  const search = useSearch();
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored || !localStorage.getItem("token")) { setLocation("/login"); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);
  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); setLocation("/"); };

  const initial = useMemo(() => new URLSearchParams(search), [search]);
  const [q, setQ] = useState(initial.get("q") || "");
  const [category, setCategory] = useState<string | null>(initial.get("category"));
  const [page, setPage] = useState(1);
  const debounced = useDebounced(q, 300);
  useEffect(() => { setPage(1); }, [debounced, category]);
  // the chat's "Abrir a biblioteca" button arrives with ?q= / ?category=
  useEffect(() => {
    const nq = initial.get("q") || "";
    const nc = initial.get("category");
    if (nq !== q) setQ(nq);
    if (nc !== category) setCategory(nc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const categories = useQuery({ queryKey: ["library-categories"], queryFn: fetchCategories, staleTime: 10 * 60 * 1000 });
  const models = useQuery({
    queryKey: ["library-models", debounced, category, page],
    queryFn: () => searchModels(debounced, category, page, PAGE_SIZE),
    placeholderData: (prev) => prev,
  });
  const mine = useQuery({ queryKey: ["library-mine-list"], queryFn: fetchMine, staleTime: 30_000 });
  const ai = useAiStatus();
  const aiState = aiButtonState(ai.data);
  const byKey = useMemo(() => new Map((categories.data?.categories || []).map((c) => [c.key, c])), [categories.data]);
  const hasQuery = debounced.trim().length > 0;

  const speech = models.data && hasQuery
    ? t.found(models.data.total)
    : category && byKey.get(category)
      ? t.browsing(categoryName(byKey.get(category), language))
      : t.hello;
  const mood = models.data && hasQuery ? (models.data.total === 0 ? "sorry" : "happy") : "wave";

  const from = models.data ? (models.data.page - 1) * models.data.pageSize + 1 : 0;
  const to = models.data ? Math.min(models.data.total, models.data.page * models.data.pageSize) : 0;
  const pages = models.data ? Math.max(1, Math.ceil(models.data.total / models.data.pageSize)) : 1;

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/modelos">
      <div className="max-w-6xl mx-auto space-y-6 p-4 md:p-6" data-testid="library-page">
        <div className="flex items-start gap-4 rounded-2xl border bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-emerald-950/30 dark:via-background dark:to-amber-950/20 p-4 md:p-6">
          <div className="shrink-0 pt-1"><Mascot mood={mood} size={72} /></div>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="library-title">{t.title}</h1>
            </div>
            <p className="text-sm text-muted-foreground">{t.subtitle}</p>
            <div className="relative inline-block max-w-xl rounded-2xl rounded-tl-sm border bg-background px-4 py-2 text-sm shadow-sm" data-testid="library-speech">
              {speech}
            </div>
            {aiState !== "hidden" && (
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-testid="library-ai-promo">
                {aiState === "locked" ? <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600" />}
                <span>{t.aiPromo}{aiState === "locked" && <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px]">{t.aiPaid}</span>}</span>
              </p>
            )}
            <div>
              <Button variant="ghost" size="sm" className="gap-2 text-primary" onClick={() => askAssistant(t.askMessage)} data-testid="library-ask">
                <Sparkles className="h-4 w-4" /> {t.ask}
              </Button>
            </div>
          </div>
        </div>

        {(mine.data?.length || 0) > 0 && (
          <section className="space-y-2 rounded-2xl border border-violet-200 bg-violet-50/50 p-4 dark:border-violet-900 dark:bg-violet-950/20" data-testid="library-mine">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="flex items-center gap-2 font-semibold"><Sparkles className="h-4 w-4 text-violet-600" /> {t.mine}</h2>
              <p className="text-xs text-muted-foreground">{t.mineHint(ai.data?.retentionDays || 30)}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {mine.data!.map((m) => (
                <Link key={m.id} href={`/modelos/meus/${m.id}`} className="group flex flex-col gap-1 rounded-xl border bg-background p-3 transition hover:border-violet-400 hover:shadow-sm" data-testid={`library-mine-${m.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium leading-snug group-hover:text-violet-700 dark:group-hover:text-violet-300">{m.title}</span>
                    {m.lang === "en" && <Badge variant="outline" className="shrink-0 text-[10px]">EN</Badge>}
                  </div>
                  {m.lastSummary && <p className="line-clamp-2 text-xs text-muted-foreground">{m.lastSummary}</p>}
                  <span className={`mt-auto inline-flex items-center gap-1 text-xs ${m.daysLeft <= 3 ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground"}`}>
                    <Clock className="h-3 w-3" /> {t.daysLeft(m.daysLeft)}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.search}
              className="h-11 pl-9 pr-9 text-base"
              data-testid="library-search"
              autoFocus
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted" aria-label="Limpar" data-testid="library-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="library-categories" aria-label={t.categories}>
            <CategoryChip active={category === null} label={t.all} onClick={() => setCategory(null)} testId="library-category-all" />
            {(categories.data?.categories || []).map((c: ContractCategory) => (
              <CategoryChip key={c.key} active={category === c.key} label={categoryName(c, language)} count={c.count} onClick={() => setCategory(category === c.key ? null : c.key)} testId={`library-category-${c.key}`} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="library-summary">
          <span>
            {models.data
              ? hasQuery ? t.results(models.data.total, debounced.trim()) : category ? t.inCategory(models.data.total) : ""
              : ""}
          </span>
          {models.isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>

        {models.data && models.data.items.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-10 text-center" data-testid="library-empty">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">{t.empty}</p>
            <p className="text-sm text-muted-foreground">{t.emptyHint}</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="library-results">
            {(models.data?.items || []).map((m) => (
              <Link key={m.slug} href={`/modelos/${m.slug}`} className="group flex h-full flex-col rounded-2xl border bg-card p-4 transition hover:border-primary/60 hover:shadow-md" data-testid={`library-item-${m.slug}`}>
                <div className="mb-2 flex items-start justify-between gap-2">
                  <h2 className="font-medium leading-snug group-hover:text-primary" data-testid="library-item-title">{m.title}</h2>
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
                <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">{m.excerpt}</p>
                <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                  <Badge variant="outline">{categoryName(byKey.get(m.category), language) || m.category}</Badge>
                  <span className="text-muted-foreground">{t.fields(m.fieldCount)}</span>
                </div>
              </Link>
            ))}
            {models.isLoading && Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-36 animate-pulse rounded-2xl border bg-muted/40" />)}
          </div>
        )}

        {models.data && models.data.total > models.data.pageSize && (
          <div className="flex items-center justify-between" data-testid="library-pagination">
            <span className="text-sm text-muted-foreground">{t.page(from, to, models.data.total)}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0 }); }} data-testid="library-prev"><ChevronLeft className="h-4 w-4" /> {t.prev}</Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0 }); }} data-testid="library-next">{t.next} <ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground" data-testid="library-note">{t.note}</p>
      </div>
    </AppLayout>
  );
}

function CategoryChip({ active, label, count, onClick, testId }: { active: boolean; label: string; count?: number; onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition ${active ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
      data-testid={testId}
      aria-pressed={active}
    >
      {label}
      {count !== undefined && <span className={`rounded-full px-1.5 text-xs ${active ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"}`}>{count}</span>}
    </button>
  );
}
